import {Component, OnDestroy} from '@angular/core';
import {Router, ActivatedRoute} from '@angular/router';
import {User} from '../../models/User';
import {AuthService} from '../../shared/services/auth.service';
import {ToastService} from '../../shared/components/toast/toast.service';
import {AGENCY_CLIENT, PARTNER_CLIENT, UserService} from '../../shared/services/user.service';
import {EMPTY, Observable, Subject} from 'rxjs';
import {catchError, filter, finalize, switchMap, takeUntil, tap} from 'rxjs/operators';
import {PartnerService} from '../../shared/services/partner.service';
import {ClientRecharge} from '../../models/ClientRecharge';
import {UtilService} from '../../shared/services/util.service';
import {ClientCredits} from '../../models/ClientCredits';
import {ClientMessages} from '../../models/ClientMessages';
import {ClientClicks} from '../../models/ClientClicks';
import {ClientBlacklist} from '../../models/ClientBlacklist';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {PaginatedResult} from '../../shared/model/paginated-result';

type StatisticsBlock = 'client' | 'credits' | 'recharges' | 'messages' | 'clicks' | 'blacklist';
type PeriodShortcut = 7 | 30 | 90;

@Component({
  selector: 'app-partner-statistics',
  templateUrl: './partner-statistics.component.html',
  styleUrls: ['./partner-statistics.component.scss']
})
export class PartnerStatisticsComponent implements OnDestroy {
  user: User;
  client: User;

  recharges$: Observable<ClientRecharge[]>;
  messages$: Observable<ClientMessages>;
  clicks$: Observable<ClientClicks>;
  credits$: Observable<ClientCredits>;
  blacklistPhones$: Observable<PaginatedResult<ClientBlacklist[]>>;

  /** Total das recargas do período, para o cartão "Recargas no período". */
  rechargesTotal: number | null = null;

  /**
   * Um aviso por quadro: cada bloco da tela espera a SUA chamada, e travar a
   * tela inteira por causa do mais lento era o que o overlay fazia.
   */
  loadingBlocks: { [block: string]: boolean } = {};

  /**
   * Falha por quadro (500, sem resposta). O bloco mostra "não foi possível
   * carregar" com "Tentar de novo" — nunca some em silêncio nem vira saldo 0.
   */
  blockErrors: { [block: string]: boolean } = {};

  startDate: Date;
  endDate: Date;
  activePeriod: PeriodShortcut | null = null;
  filterStarted = false;

  destroyed = new Subject<void>();

  perPage = 10;
  phones = '';
  phonesFilter = '';

  /** :userId da rota, e o canal que leva a troca (e o "Tentar de novo") ao `switchMap`. */
  private clientId: string | null = null;
  private clientId$ = new Subject<string>();

  /** Só a resposta da chamada MAIS RECENTE de cada bloco mexe na tela. */
  private requestSeq: { [block: string]: number } = {};

  get title(): string {
    if (!this.client) {
      return 'Estatísticas';
    }

    return `Estatísticas · ${this.client.name}`;
  }

  get isAgency(): boolean {
    return this.user?.profile_id === AGENCY_CLIENT;
  }

  /**
   * "Acessar conta" só para a agência em pessoa, como na lista de Parceiros
   * (Multiusuários, CU-17tm466h1cm): membro da conta não simula a filha.
   */
  get canAccessClientAccount(): boolean {
    return this.isAgency && this.authService.isAccountOwner;
  }

  get is90DayInterval(): boolean {
    if (!this.startDate || !this.endDate) {
      return false;
    }

    const timeDiff = Math.abs(this.endDate.getTime() - this.startDate.getTime());
    const diffDays = Math.ceil(timeDiff / (1000 * 3600 * 24));

    return diffDays > 90;
  }

  get invalidPeriod(): boolean {
    return !!this.startDate && !!this.endDate && this.startDate > this.endDate;
  }

  /** A recarga de carteira grava `credits: 0`: a nota de rodapé só aparece quando há uma. */
  hasWalletRecharge(recharges: ClientRecharge[]): boolean {
    return (recharges || []).some(recharge => recharge.credits === 0);
  }

  constructor(
    private authService: AuthService,
    private toastService: ToastService,
    private route: ActivatedRoute,
    private router: Router,
    private userService: UserService,
    private partnerService: PartnerService,
    private utilService: UtilService,
    private modalService: NgbModal,
  ) {
    this.user = this.authService.getUser();

    // Esta tela mostra os dados de uma conta *filha*, então exige mais que
    // `api_enabled`: cliente comum agora também pode ter a API liberada, mas
    // não gerencia contas de terceiros.
    const managesClients = this.user.profile_id === AGENCY_CLIENT || this.user.profile_id === PARTNER_CLIENT;

    // `history.back()` é assíncrono: sem o `return` o construtor seguia e
    // disparava o findById do usuário barrado, deixando a requisição sair
    // depois do bloqueio.
    if (!this.user.api_enabled || !managesClients) {
      this.toastService.error('Você não possui acesso à esta página. Entre em contato com o Administrador!');
      history.back();
      return;
    }

    // O Angular Router reaproveita esta instância ao navegar entre duas
    // Estatísticas (mesma rota, outro :userId). `switchMap` cancela o findById
    // do cliente anterior: sem isso, se o do Filho A voltasse DEPOIS do do
    // Filho B, a tela (URL do B) passava a mostrar a carteira do A.
    this.clientId$.pipe(
      tap(() => this.resetClient()),
      switchMap(userId => this.track('client', this.userService.findById(userId))),
      takeUntil(this.destroyed),
    ).subscribe(user => {
      this.client = user;
      // Saldo não espera o período (decisão do mockup aprovado, 27/10): o
      // bloco de "Saldo agora" abre na hora, com os últimos 30 dias já
      // selecionados. credits$/blacklistPhones$ são por CLIENTE, não por
      // período — por isso ficam aqui, fora de filterChanged().
      this.loadCredits();
      this.blacklistPhones$ = this.track('blacklist', this.partnerService.getClientBlacklist(this.client.id));
      this.setPeriod(30);
    });

    this.route.params.pipe(
      tap(params => {
        if (!params.userId) {
          this.toastService.error('Você não possui acesso à esta página. Entre em contato com o Administrador!');
          history.back();
        }
      }),
      filter(params => !!params.userId),
      takeUntil(this.destroyed),
    ).subscribe(params => {
      this.clientId = params.userId;
      this.clientId$.next(params.userId);
    });
  }

  ngOnDestroy(): void {
    this.destroyed.next();
    this.destroyed.complete();
  }

  /** "Tentar de novo" quando o cliente não abriu (a rota não muda, então não reemite). */
  retryClient(): void {
    if (this.clientId) {
      this.clientId$.next(this.clientId);
    }
  }

  loadCredits(): void {
    if (!this.client) {
      return;
    }

    this.credits$ = this.track('credits', this.partnerService.getClientCredits(this.client.id));
  }

  /** Atalhos de período (7/30/90 dias), com os últimos 90 como o teto aceito. */
  setPeriod(days: PeriodShortcut): void {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - days);

    this.startDate = start;
    this.endDate = end;
    this.activePeriod = days;
    this.filterChanged();
  }

  /** `<input type="date">` trabalha com `yyyy-MM-dd`; o resto da tela, com `Date`. */
  toInputDate(date: Date | null): string {
    return date ? this.utilService.dateToIso(date) : '';
  }

  startDateChanged(value: string): void {
    this.startDate = this.fromInputDate(value);
    this.activePeriod = null;
  }

  endDateChanged(value: string): void {
    this.endDate = this.fromInputDate(value);
    this.activePeriod = null;
  }

  filterChanged(): void {
    if (!this.startDate || !this.endDate) {
      this.toastService.error('Você precisa informar a data inicial e a data final');
      return;
    }

    if (!this.client || this.invalidPeriod || this.is90DayInterval) {
      return;
    }

    this.filterStarted = true;
    const startDate = this.utilService.dateToIso(this.startDate);
    const endDate = this.utilService.dateToIso(this.endDate);

    this.rechargesTotal = null;
    this.recharges$ = this.track('recharges',
      this.partnerService.getClientRecharges(this.client.id, startDate, endDate).pipe(
        tap(recharges => {
          this.rechargesTotal = (recharges || []).reduce((sum, recharge) => sum + Number(recharge.total || 0), 0);
        }),
      ));

    this.messages$ = this.track('messages',
      this.partnerService.getClientMessages(this.client.id, startDate, endDate));

    this.clicks$ = this.track('clicks',
      this.partnerService.getClientClicks(this.client.id, startDate, endDate));
  }

  showAddPhonesModal(modal: any): void {
    this.phones = '';
    this.modalService.open(modal);
  }

  get phonesArray(): string[] {
    let phones = this.phones.split('\n').filter((n) => n.trim() !== '');

    if (!this.phones.includes('\n')) {
      phones = this.phones.split(',').filter((n) => n.trim() !== '');
    }

    return phones;
  }

  addPhones(modal: any): void {
    const phones = this.phonesArray;
    if (phones.length === 0) {
      this.toastService.error('Você deve digitar ao menos 1 número!');
      return;
    }

    this.partnerService
      .saveClientBlacklist(this.client.id, phones)
      .pipe(takeUntil(this.destroyed))
      .subscribe({ next: (res) => {
          this.toastService.success(res.message);
          this.blacklistPhones$ = this.track('blacklist',
            this.partnerService.getClientBlacklist(this.client.id, this.phonesFilter, 1, this.perPage));
          modal.close();
        } });
  }

  delPhones(modal: any): void {
    const phones = this.phonesArray;
    if (phones.length === 0) {
      this.toastService.error('Você deve digitar ao menos 1 número!');
      return;
    }

    this.partnerService
      .deleteClientBlacklist(this.client.id, phones)
      .pipe(takeUntil(this.destroyed))
      .subscribe({ next: (res) => {
          this.toastService.success(res.message);
          this.blacklistPhones$ = this.track('blacklist',
            this.partnerService.getClientBlacklist(this.client.id, this.phonesFilter, 1, this.perPage));
          modal.close();
        } });
  }

  filterKeyPress(event: KeyboardEvent): void {
    const key = event?.key?.toLocaleUpperCase();
    if (key === 'ENTER' || this.phonesFilter === '') {
      this.pageChanged(1);
    }
  }

  pageChanged(page: number): void {
    this.blacklistPhones$ = this.track('blacklist',
      this.partnerService.getClientBlacklist(this.client.id, this.phonesFilter, page, this.perPage));
  }

  simulate(): void {
    if (!this.client || !this.canAccessClientAccount) {
      return;
    }

    this.authService.setSimulatedUser(this.client);
    this.router.navigate(['/']);
  }

  /** Troca de cliente: nada do anterior fica na tela enquanto o novo carrega. */
  private resetClient(): void {
    this.client = null;
    this.credits$ = null;
    this.blacklistPhones$ = null;
    this.recharges$ = null;
    this.messages$ = null;
    this.clicks$ = null;
    this.rechargesTotal = null;
    this.blockErrors = {};
  }

  private fromInputDate(value: string): Date | null {
    if (!value) {
      return null;
    }

    const [year, month, day] = value.split('-').map(Number);

    return new Date(year, month - 1, day);
  }

  /**
   * Liga o aviso do bloco e desliga no DESFECHO da chamada — emissão, erro ou
   * término sem emissão (requisição cancelada). Erro vira `blockErrors[block]`
   * e o fluxo termina limpo (o `async` do template não quebra). Só a chamada
   * mais recente de cada bloco mexe nas flags: a anterior, cancelada pelo
   * `async`/`switchMap`, não desliga o aviso da que está chegando.
   */
  private track<T>(block: StatisticsBlock, source: Observable<T>): Observable<T> {
    const seq = (this.requestSeq[block] || 0) + 1;
    this.requestSeq[block] = seq;
    this.loadingBlocks[block] = true;
    this.blockErrors[block] = false;
    const isCurrent = () => this.requestSeq[block] === seq;

    return source.pipe(
      tap(() => {
        if (isCurrent()) {
          this.loadingBlocks[block] = false;
        }
      }),
      catchError(() => {
        if (isCurrent()) {
          this.blockErrors[block] = true;
        }

        return EMPTY;
      }),
      finalize(() => {
        if (isCurrent()) {
          this.loadingBlocks[block] = false;
        }
      }),
    );
  }
}
